import React, { useEffect, useState, useMemo, useRef, useCallback, useDeferredValue } from 'react'
import { Box, Input, IconButton, Typography, Divider } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { Icon as Iconify } from '@iconify/react'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { EXPANDED_LS_KEY, keyOf, loadExpandedStateFromLS, filterItems } from './pinFilterUtils'
import { addItemToLS } from 'utils/localStorage'

const PinButtonDropdown = ({ items, pinned = [], onChange }) => {
  const { getTextColor, searchBarStyle, theme: innovaTheme } = useInnovaTheme()
  const [expandedKeys, setExpandedKeys] = useState(() => loadExpandedStateFromLS())
  const [value, setValue] = useState('')
  const deferredValue = useDeferredValue(value)
  const searchInputRef = useRef(null)

  const style = searchBarStyle()

  const expandedSet = expandedKeys

  useEffect(() => {
    addItemToLS('wellListPinPopup', EXPANDED_LS_KEY, Array.from(expandedSet) || [])
  }, [expandedSet])

  const pinnedSet = useMemo(() => {
    const s = new Set()
    for (const p of pinned || []) s.add(keyOf(p.column, p.value))
    return s
  }, [pinned])

  const filtered = useMemo(() => filterItems(items || {}, deferredValue, pinnedSet), [items, deferredValue, pinnedSet])

  const toggleExpand = useCallback((column) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(column)) next.delete(column)
      else next.add(column)
      return next
    })
  }, [])

  const pinValue = useCallback(
    (column, val, external = false) => {
      if (!onChange) return
      const key = keyOf(column, val)
      if (pinnedSet.has(key)) return
      const next = [{ column, columnLabel: items[column].label, value: val, external }, ...(pinned || [])]
      onChange(next)
    },
    [onChange, pinned, pinnedSet, items],
  )

  const unpinValue = useCallback(
    (column, val) => {
      if (!onChange) return
      const key = keyOf(column, val)
      if (!pinnedSet.has(key)) return
      const next = (pinned || []).filter((p) => !(p.column === column && p.value === val))
      onChange(next)
    },
    [onChange, pinned, pinnedSet],
  )

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return
    for (const group of filtered?.allFilters) {
      if (group?.values?.length > 0) {
        pinValue(group.column, group.values[0])
        break
      }
    }
  }

  useEffect(() => {
    searchInputRef.current?.focus?.()
  }, [])

  const isDark = innovaTheme === 'dark'

  const ValueRow = ({ column, val, onClick, isPinned }) => (
    <Box
      key={`${isPinned ? 'pinned' : 'value'}-${column}:${val}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
      onClick={onClick}
      title={String(val)}>
      <Typography
        sx={{
          fontSize: '12px',
          color: getTextColor(),
          pr: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}>
        {String(val)}
      </Typography>
      <Iconify
        icon={isPinned ? 'fluent:pin-off-28-regular' : 'fluent:pin-28-filled'}
        width='16'
        style={{ color: getTextColor() }}
      />
    </Box>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 320,
        maxHeight: 320,
        overflow: 'hidden',
        bgcolor: style.backgroundColor,
        borderBottomLeftRadius: 5,
        borderBottomRightRadius: 5,
        boxSizing: 'border-box',
        outline: isDark ? style.border : 'none',
        outlineOffset: isDark ? '.5px' : '0',
        pt: 1,
        pb: 1,
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, pb: 1 }}>
        <Input
          inputRef={(node) => {
            if (!node) return
            searchInputRef.current = node
          }}
          id='pin-search-input'
          autoComplete='off'
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='Search filters…'
          fullWidth
          disableUnderline
          sx={{
            height: 28,
            px: 1,
            borderRadius: 1,
            border: (t) => `1px solid ${t.palette.divider}`,
            '& .MuiInputBase-input': {
              color: (t) => `${getTextColor()} !important`,
              fontSize: '12px',
            },
          }}
        />
        <IconButton
          onClick={() => setValue('')}
          size='small'
          sx={{ color: getTextColor(), visibility: value ? 'visible' : 'hidden' }}>
          <ClearIcon fontSize='small' />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: 0.5,
          px: 1,
          pb: 0.5,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '8px',
            backgroundColor: isDark ? '#424242' : undefined,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? '#c0c0c0' : undefined,
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: isDark ? '#000' : undefined,
          },
        }}>
        {Array.isArray(filtered?.pinned) && filtered.pinned.length > 0 && (
          <>
            <Typography sx={{ fontSize: '11px', opacity: 0.75, color: getTextColor(), py: 0.5 }}>Pinned</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 1, pb: 0.5 }}>
              {filtered.pinned.map(({ column, values }) =>
                // sort values for consistent order
                values
                  .sort()
                  .map((val) => (
                    <ValueRow
                      key={`pinned-${column}:${val}`}
                      column={column}
                      val={val}
                      onClick={() => unpinValue(column, val)}
                      isPinned={true}
                    />
                  )),
              )}
            </Box>
            <Divider sx={{ mt: 0.5, mb: 0.5, opacity: 0.4 }} />
          </>
        )}

        <Typography sx={{ fontSize: '11px', opacity: 0.75, color: getTextColor(), pb: 0.5 }}>All Filters</Typography>

        {filtered?.allFilters?.length ? (
          filtered.allFilters.map(({ column, label, values, external }) => {
            const expanded =
              values.length > 0 &&
              ((typeof expandedSet !== 'undefined' && expandedSet?.has(column)) || value.trim().length > 0)
            return (
              <React.Fragment key={column}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 0.5,
                    py: 0.25,
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => toggleExpand(column)}
                  title={column}>
                  <Typography sx={{ fontSize: '12px', color: getTextColor(), flex: 1 }}>
                    {expanded ? '▾' : '▸'} {label}
                  </Typography>
                </Box>

                {expanded && (
                  <Box sx={{ pl: 2 }}>
                    {values.sort().map((val) => {
                      return (
                        <ValueRow
                          key={`${column}:${val}`}
                          column={column}
                          val={val}
                          onClick={() => {
                            if (pinnedSet.has(keyOf(column, val))) return unpinValue(column, val)
                            return pinValue(column, val, external)
                          }}
                          isPinned={pinnedSet.has(keyOf(column, val))}
                        />
                      )
                    })}
                  </Box>
                )}
              </React.Fragment>
            )
          })
        ) : (
          <>
            <Divider sx={{ my: 0.5, opacity: 0.4 }} />
            <Typography sx={{ fontSize: '11px', opacity: 0.75, color: getTextColor(), px: 1, py: 0.5 }}>
              {Array.isArray(pinned) && pinned.length ? '' : 'No results.'}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  )
}

export default PinButtonDropdown
